import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext.js';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import Loading from "../../components/Loading";

// Helper: always display oldest to newest (top to bottom)
function sortMessages(arr) {
  // Use your actual backend's field for time/id
  // Prefer "created_at" or fallback to "timestamp", else index order
  return arr.slice().sort((a, b) => {
    if (a.created_at && b.created_at) {
      return new Date(a.created_at) - new Date(b.created_at);
    }
    if (a.timestamp && b.timestamp) {
      return new Date(a.timestamp) - new Date(b.timestamp);
    }
    // If none available, keep original order
    return 0;
  });
}

export default function Messages() {
  const { getAccessToken, getUsername, socket } = useAuth();
  const { status: order_id, panel } = useParams();
  const navigate = useNavigate();

  const [header, setHeader] = useState(null);
  const [headers, setHeaders] = useState(null);
  const [headersUpdated, setHeadersUpdated] = useState(null);
  const [shownMessages, setShownMessages] = useState([]);
  const [newMessage, setNewMessage] = useState(null);
  const [loadable, setLoadable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(false);

  const messageLim = 12;
  const messagesEndRef = useRef();
  const scrollLockRef = useRef(false); // Prevent auto-scroll on load more

  // Auto-scroll to bottom for new messages, not for load more
  useEffect(() => {
    if (!scrollLockRef.current && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
    scrollLockRef.current = false;
  }, [shownMessages, order_id]);

  // Listen for new messages via custom event (socket)
  useEffect(() => {
    const handleMessages = (event) => {
      let data = event.detail.data;
      if (data.sender === 'User') return;
      event.detail.notify();
      if (!order_id) return;
      if (data.order_id !== order_id) {
        setHeadersUpdated(data.order_id);
        return;
      }
      setNewMessage(data);
    };
    document.addEventListener('message', handleMessages);
    return () => {
      document.removeEventListener('message', handleMessages);
    };
  }, [order_id]);

  // Load messages for this order
  useEffect(() => {
    if (!order_id) return;
    if (panel !== 'messages') return;
    setLoading(true);
    axios.post(process.env.REACT_APP_API_URL + `/user/messages/${order_id}`, {
      messageCount: 0,
      messageLim: messageLim
    }, {
      headers: { Authorization: getAccessToken() }
    }).then((response) => {
      setShownMessages(sortMessages(response.data.messages));
      if (response.data.cooldown) {
        setCooldown(true);
        setTimeout(() => setCooldown(false), 5000);
      }
      setLoadable(response.data.messages.length === messageLim);
      setLoading(false);
    }).catch((error) => {
      setShownMessages([]);
      setLoading(false);
    });
    // eslint-disable-next-line
  }, [order_id, panel, getAccessToken]);

  // Set headers and update read status
  useEffect(() => {
    if (!order_id || !headers) return;
    if (order_id === header?.order_id) return;
    const updatedHeaders = headers.map((element) => {
      if (element.order_id === order_id) {
        return { ...element, unread: false };
      }
      return element;
    });
    setHeader(updatedHeaders.find(element => element.order_id === order_id));
  }, [order_id, headers, header]);

  // Fallback header
  useEffect(() => {
    if (!order_id) return;
    if (!header) {
      setHeader({ header: 'Inactive Order', subtitle: order_id });
    }
  }, [header, order_id]);

  // Update unread status for other orders
  useEffect(() => {
    if (!headersUpdated || !headers) return;
    const updatedHeaders = headers.map((element) => {
      if (element.order_id === headersUpdated) {
        return { ...element, unread: true };
      }
      return element;
    });
    setHeadersUpdated(null);
    setHeaders(updatedHeaders);
  }, [headersUpdated, headers]);

  // Receive message via socket.io or custom event
  useEffect(() => {
    if (!newMessage) return;
    if (!order_id) return;
    setShownMessages(prev =>
      sortMessages([
        ...prev,
        {
          ...newMessage,
          created_at: newMessage.created_at || new Date().toISOString(),
        }
      ])
    );
    axios.put(process.env.REACT_APP_API_URL + `/user/read-messages/${order_id}`, {}, {
      headers: { Authorization: getAccessToken() }
    }).catch((error) => { console.log(error); });
  }, [newMessage, order_id, getAccessToken]);

  // Load more (prepend)
  function loadMessages() {
    axios.post(process.env.REACT_APP_API_URL + `/user/messages/${order_id}`, {
      messageCount: shownMessages.length,
      messageLim: messageLim
    }, {
      headers: { Authorization: getAccessToken() }
    }).then((response) => {
      setShownMessages(prev =>
        sortMessages([
          ...response.data.messages,
          ...prev
        ])
      );
      setLoadable(response.data.messages.length === messageLim);
      scrollLockRef.current = true; // Prevent scroll-to-bottom on prepend
    }).catch((error) => {
      console.log(error);
    });
  }

  // Load active orders/headers
  useEffect(() => {
    setLoading(true);
    axios.get(process.env.REACT_APP_API_URL + '/user/message-headers', {
      headers: { Authorization: getAccessToken() }
    }).then((response) => {
      if (!order_id && response.data.length > 0) {
        navigate(`/my-orders/messages/${response.data[0].order_id}`);
      }
      setHeaders(response.data);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
    // eslint-disable-next-line
  }, []);

  // Arrows & active order
  const idx = headers?.findIndex(o => o.order_id === order_id) ?? 0;
  const hasPrev = idx > 0;
  const hasNext = headers && idx < headers.length - 1;
  const currentOrder = headers ? headers[idx] : { header: '', subtitle: '' };

  // Arrow navigation logic
  const handleOrderSwitch = dir => {
    if (!headers) return;
    const newIdx = dir === "prev" ? idx - 1 : idx + 1;
    if (newIdx >= 0 && newIdx < headers.length) {
      navigate(`/my-orders/messages/${headers[newIdx].order_id}`);
    }
  };

  // Remove/restore body class for Crisp/etc
  useEffect(() => {
    document.body.classList.add("messages-route");
    return () => {
      document.body.classList.remove("messages-route");
      window.location.reload(); // Reload to reset any Crisp state
    };
  }, []);

  return (
    <>
      <Loading loading={loading} panel={true} />
      <div className="w-full h-[calc(100vh-106px)] max-[925px]:h-[calc(100vh-170px)] mx-auto max-w-6xl flex flex-col bg-[var(--bg-page)] text-white rounded-t-md md:overflow-hidden">
        {/* Header */}
        <header className="flex items-center justify-between bg-[var(--main)] px-6 py-4 rounded-t-md">
          {hasPrev ? (
            <button
              onClick={() => handleOrderSwitch("prev")}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition"
              aria-label="Previous Order"
              type="button"
            >
              <svg className="w-6 h-6" fill="none" stroke="white" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          ) : <div className="w-10" />}
          <div className="text-center w-full">
            <h2 className="text-2xl font-bold truncate">{currentOrder.header}</h2>
            <p className="text-sm opacity-75 truncate">{currentOrder.subtitle}</p>
          </div>
          {hasNext ? (
            <button
              onClick={() => handleOrderSwitch("next")}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition"
              aria-label="Next Order"
              type="button"
            >
              <svg className="w-6 h-6" fill="none" stroke="white" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ) : <div className="w-10" />}
        </header>

        {/* Messages */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4 bg-[var(--bg-surface)] messages-container relative" style={{ scrollBehavior: "smooth" }}>
          {loadable && (
            <div className="flex justify-center pb-2">
              <button
                onClick={loadMessages}
                className="px-4 py-1 rounded bg-white/10 hover:bg-white/20 text-sm text-white transition"
              >
                Load More
              </button>
            </div>
          )}
          {shownMessages.map((m, i) => (
            
            <div
              key={i}
              className={`max-w-md p-3 rounded-lg ${
                m.sender === "User"
                  ? "ml-auto bg-blue-600 text-white"
                  : "mr-auto bg-gray-700 text-gray-200"
              }`}
            >
              <div className="text-xs opacity-75 mb-1">
                {m.sender === "User" ? getUsername() : m.sender}
              </div>
              <div>{m.message}</div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form
            onSubmit={e => {
                e.preventDefault();

                const txt = e.currentTarget.elements.msg;
                const messageContent = txt.value.trim();

                if (!messageContent || cooldown) return;

                setCooldown(true);
                setTimeout(() => setCooldown(false), 5000);

                console.log("Sending message:", messageContent);

                const now = new Date().toISOString();

                setShownMessages(prev =>
                sortMessages([
                    ...prev,
                    { sender: 'User', message: messageContent, created_at: now }
                ])
                );

                socket?.emit('order-message', {
                order_id,
                sender: 'User',
                message: messageContent
                });

                axios.post(process.env.REACT_APP_API_URL + `/user/send-message/${order_id}`, {
                message: messageContent
                }, {
                headers: { Authorization: getAccessToken() }
                }).catch((error) => { console.log(error); });

                txt.value = "";
            }}
            className="flex items-center space-x-2 bg-[var(--bg-surface-variant)] p-4"
            >
            <input
                id="messages-input"
                name="msg"
                type="text"
                autoComplete="off"
                placeholder="Type a message…"
                maxLength={300}
                className="flex-1 px-4 py-2 rounded-lg bg-[var(--bg-surface)] focus:outline-none focus:ring-1 focus:ring-[var(--main)]"
                disabled={cooldown}
            />
            <button
                id="messages-button"
                type="submit"
                disabled={cooldown}
                className="px-4 py-2 bg-[var(--main)] rounded-lg"
            >
                Send
            </button>
            </form>

      </div>
    </>
  );
}
